import { Project } from './Types';
import { useBackendApi } from './backendApi';
import { useDateHelper, useObjectDatesMapper } from './Helper';
import { ProjectContractParseResult, RelativeDateOffset } from './aiLauncherService';

export function useAdditionalWorkflowAiService() {
  const backendService = useBackendApi();

  const dateHelper = useDateHelper();

  const {
    mapSingle: baseMapSingle,
  } = useObjectDatesMapper<Project>(
    [
      'startDate',
      'endDate',
      'statistic.nextKeyDate.dueDate.date',
      'statistic.nextKeyDate.dueDate.time',
    ],
    [],
  );

  const analyzeContract = async (projectId: number, planId: number, fileId: number) => {
    return await backendService.post(
      `/projects/${projectId}/plans/${planId}/ai-analyze`,
      { fileId },
    );
  };

  const getContractParseResult = async (projectId: number, planId: number) => {
    const result = await backendService.get(
      `/projects/${projectId}/plans/${planId}/contractparseresult`,
    ) as ProjectContractParseResult;

    return {
      ...result,
      detailsFields: Object.fromEntries(
        Object.entries(result.detailsFields).map(([key, value]) => {
          if (value.type === 'date') {
            return [key, {
              ...value,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              value: value.value ? dateHelper.parseDateString(value.value) : undefined,
            }];
          } else {
            return [key, value];
          }
        }),
      ),
    };
  };

  const removeContact = async (projectId: number, planId: number, internalId: number) => {
    return await backendService.delete(
      `/projects/${projectId}/plans/${planId}/contractparseresult/people/${internalId}`,
    );
  };

  const updateName = async (projectId: number, planId: number, name: string) => {
    return await backendService.put(
      `/projects/${projectId}/plans/${planId}/contractparseresult/name`,
      { value: name },
    );
  };

  const updateStringFieldValue = async (projectId: number, planId: number, fieldId: number, value?: string) => {
    return await backendService.put(
      `/projects/${projectId}/plans/${planId}/contractparseresult/fields/${fieldId}/string`,
      { value },
    );
  };

  const updateDateFieldValue = async (projectId: number, planId: number, fieldId: number, value?: Date) => {
    return await backendService.put(
      `/projects/${projectId}/plans/${planId}/contractparseresult/fields/${fieldId}/date`,
      { value },
    );
  };

  const updateNumberFieldValue = async (projectId: number, planId: number, fieldId: number, value?: number) => {
    return await backendService.put(
      `/projects/${projectId}/plans/${planId}/contractparseresult/fields/${fieldId}/number`,
      { value },
    );
  };

  const updateRelativeDateOffsetValue = async (
    projectId: number,
    planId: number,
    fieldId: number,
    value?: RelativeDateOffset,
  ) => {
    return await backendService.put(
      `/projects/${projectId}/plans/${planId}/contractparseresult/fields/${fieldId}/relativedateoffset`,
      { value: value || null },
    );
  };

  const applyDefinedData = async (projectId: number, planId: number) => {
    const result = await backendService.post(
      `/projects/${projectId}/plans/${planId}/contractparseresult/apply`,
      {},
    );

    return baseMapSingle(result);
  };

  return {
    analyzeContract,
    removeContact,
    getContractParseResult,
    updateName,
    updateStringFieldValue,
    updateDateFieldValue,
    updateNumberFieldValue,
    updateRelativeDateOffsetValue,
    applyDefinedData,
  };
}
